/**
 * Entry point du SDK widget Rintintin.
 *
 * Une fois chargé, ce bundle :
 *   1. Définit `window.RintintinBooking = { init, version }`.
 *   2. Draine `window.RintintinBookingQueue` (les configs poussées AVANT le
 *      chargement du script, pattern « queue » à la Google Analytics).
 *   3. Remplace `RintintinBookingQueue` par un proxy `{ push: (opts) => init(opts) }`
 *      pour gérer les `push(...)` arrivés APRÈS le chargement.
 *
 * Phase 3 : ajout des modes `popup` et `badge` en plus de `inline`.
 */

import { mountInlineWidget } from './iframe';
import { mountPopupWidget } from './popup';
import { mountBadgeWidget } from './badge';
import type {
  RintintinBookingGlobal,
  RintintinBookingInitOptions,
  RintintinBookingInstance,
  RintintinBookingQueueEntry,
  RintintinBookingQueueProxy,
} from './types';

// Injecté à la build par esbuild via `define`. Fallback de dev = `0.0.0-dev`.
declare const __RTT_WIDGET_VERSION__: string;
const SDK_VERSION: string =
  typeof __RTT_WIDGET_VERSION__ !== 'undefined' ? __RTT_WIDGET_VERSION__ : '0.0.0-dev';

const LOG_PREFIX = '[RintintinBooking]';

/** Instance no-op renvoyée quand la config est invalide (évite les TypeError côté hôte). */
const NOOP_INSTANCE: RintintinBookingInstance = {
  destroy() {
    /* no-op */
  },
};

/**
 * Compteur global pour générer des ids uniques au container quand le SDK doit
 * en créer un lui-même (future API ; pour l'instant utilisé uniquement par
 * `nextInstanceId()` côté inline multi-widget — réservé pour permettre
 * plusieurs widgets sur une même page si l'hôte délègue la création du div).
 */
let instanceCounter = 0;
export function nextInstanceId(prefix = 'rtt-widget'): string {
  instanceCounter += 1;
  return `${prefix}-${instanceCounter}`;
}

/**
 * Suivi des instances inline par container DOM, pour permettre destroy+replace
 * si `init()` est rappelé sur le même container (cas typique : rerender côté hôte).
 */
const inlineInstances = new WeakMap<HTMLElement, RintintinBookingInstance>();

/** Résout un container CSS selector → HTMLElement (ou HTMLElement direct). */
function resolveContainerElement(container: string | HTMLElement | undefined): HTMLElement | null {
  if (!container) return null;
  if (typeof container !== 'string') return container;
  try {
    return document.querySelector<HTMLElement>(container);
  } catch {
    return null;
  }
}

/**
 * Initialise un widget. Validation runtime + dispatch selon `mode`.
 * Retourne toujours une instance (no-op en cas de config invalide).
 */
export function init(options: RintintinBookingInitOptions): RintintinBookingInstance {
  if (!options || typeof options !== 'object') {
    console.warn(`${LOG_PREFIX} init() appelé sans options.`);
    return NOOP_INSTANCE;
  }

  if (!options.pro || typeof options.pro !== 'string') {
    console.warn(`${LOG_PREFIX} option \`pro\` manquante ou invalide :`, options.pro);
    return NOOP_INSTANCE;
  }

  const mode = options.mode || 'inline';

  if (mode === 'inline') {
    if (!options.container) {
      console.warn(
        `${LOG_PREFIX} option \`container\` manquante en mode inline. Ajoutez container: '#rtt-widget'.`
      );
      return NOOP_INSTANCE;
    }
    // Multi-instance : si init() est rappelé pour le même container, on détruit
    // le précédent silencieusement (rerender côté hôte) — éviter d'empiler les iframes.
    const containerEl = resolveContainerElement(options.container);
    if (containerEl) {
      const previous = inlineInstances.get(containerEl);
      if (previous) {
        console.warn(
          `${LOG_PREFIX} container déjà utilisé par une instance inline — destroy + remplacement.`
        );
        previous.destroy();
        inlineInstances.delete(containerEl);
      }
    }
    const instance = mountInlineWidget(options);
    if (instance && containerEl) {
      inlineInstances.set(containerEl, instance);
      // On wrap destroy pour nettoyer la map quand l'hôte démonte
      const original = instance.destroy.bind(instance);
      instance.destroy = () => {
        inlineInstances.delete(containerEl);
        original();
      };
    }
    return instance || NOOP_INSTANCE;
  }

  if (mode === 'popup') {
    return mountPopupWidget(options);
  }

  if (mode === 'badge') {
    return mountBadgeWidget(options);
  }

  console.warn(`${LOG_PREFIX} mode non supporté :`, mode);
  return NOOP_INSTANCE;
}

/**
 * Draine les entrées poussées dans `RintintinBookingQueue` avant le chargement
 * du SDK. Robuste à un push d'un objet non-conforme (warning + skip).
 */
function drainQueue(queue: RintintinBookingQueueEntry[]): void {
  for (const entry of queue) {
    try {
      init(entry as RintintinBookingInitOptions);
    } catch (err) {
      console.error(`${LOG_PREFIX} erreur lors du drainage queue :`, err);
    }
  }
}

/**
 * Installe le SDK sur `window` : remplace la queue par un proxy push-driven
 * et expose `window.RintintinBooking`. Idempotent.
 */
export function install(target: Window): RintintinBookingGlobal {
  const existingQueue = target.RintintinBookingQueue;

  if (Array.isArray(existingQueue)) {
    drainQueue(existingQueue);
  }

  const proxy: RintintinBookingQueueProxy = {
    push(options: RintintinBookingInitOptions) {
      return init(options);
    },
  };
  target.RintintinBookingQueue = proxy;

  const global: RintintinBookingGlobal = {
    init,
    version: SDK_VERSION,
  };
  target.RintintinBooking = global;
  return global;
}

if (typeof window !== 'undefined') {
  install(window);
}
